import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import { Modal, Button, Form } from "react-bootstrap";
import "../../styles/Form.css";
import "../../styles/AdminUserTable.css";
import Pagination from "../../components/Pagination";
import useAccess from "../../hooks/useAccess";

function AdminUserPage() {
  //  API & Auth Constants
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  //  React Router
  const navigate = useNavigate();

  //  UI State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  //  Data State
  const [adminUsers, setAdminUsers] = useState([]);
  const [roleMap, setRoleMap] = useState({});

  // Status Modal State
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newStatus, setNewStatus] = useState(1);

  //  Filter & Search State
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [statusOptions, setStatusOptions] = useState([]);

  //  Access Control (reactive)
  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    users: {
      create: hasPermission("AdminUser", "User", "create"),
      edit: hasPermission("AdminUser", "User", "edit"),
      delete: hasPermission("AdminUser", "User", "delete"),
    },
  };

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const adminsOnly = res.data.filter((user) => user.role === 1);
        setAdminUsers(adminsOnly);
      })
      .catch((err) => console.error("Failed to fetch admin users:", err));

    axios
      .get(`${BASE_URL}/api/dropdowns/user-status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setStatusOptions(res.data))
      .catch((err) =>
        console.error("❌ Failed to fetch user status options:", err)
      );
  }, [BASE_URL, token]);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/dropdowns/emp-role`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const map = {};
        res.data.forEach((r) => {
          map[r.r_id] = r.r_name;
        });
        setRoleMap(map);
      })
      .catch((err) => console.error("❌ Failed to fetch emp roles:", err));
  }, [BASE_URL, token]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const renderEmpRoles = (rolesJson) => {
    if (!rolesJson) return "";
    try {
      const ids = Array.isArray(rolesJson)
        ? rolesJson
        : JSON.parse(rolesJson || "[]");
      if (!Array.isArray(ids)) return "";
      const names = ids.map((id) => roleMap[id]).filter(Boolean);
      return names.join(", ");
    } catch {
      return "";
    }
  };

  const handleEdit = (id) => navigate(`/edit-user/${id}`);
  const handleDelete = (id) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    axios
      .delete(`${BASE_URL}/api/users/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(() => {
        alert("✅ User deleted successfully");
        setAdminUsers((prev) => prev.filter((u) => u.id !== id));
      })
      .catch(() => alert("❌ Failed to delete user"));
  };

  const openStatusModal = (user) => {
    if (!PERMISSIONS.users.edit) return;

    setSelectedUser(user);
    setNewStatus(user.status);
    setShowStatusModal(true);
  };

  const saveStatus = async () => {
    try {
      await axios.put(
        `${BASE_URL}/api/admin-users/${selectedUser.id}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setAdminUsers((prev) =>
        prev.map((u) =>
          u.id === selectedUser.id ? { ...u, status: newStatus } : u
        )
      );

      setShowStatusModal(false);
      alert("✅ Status updated successfully");
    } catch (err) {
      alert("❌ Failed to update status");
    }
  };

  const getFilteredUsers = () =>
    adminUsers.filter((user) => {
      const matchesSearch = Object.values(user).some((val) =>
        val?.toString().toLowerCase().includes(searchQuery.toLowerCase())
      );
      const matchesStatus =
        statusFilter === "All" ||
        user.status?.toString() === getStatusIdByName(statusFilter);
      return matchesSearch && matchesStatus;
    });

  const getStatusIdByName = (name) => {
    const s = statusOptions.find((s) => s.s_TranslatedText === name);
    return s ? s.s_id.toString() : null;
  };

  const getPaginatedData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  return (
    <div className="form-wrapper my-3">
      <div className="dashboard-header d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <h3 className="fw-bold pb-2">🛡️ Admin Users</h3>
        {PERMISSIONS.users.create && (
          <button
            className="btn btn-success dashboard-add-button"
            onClick={() => navigate("/form")}
          >
            Add New User
          </button>
        )}
      </div>

      <div className="row mb-3">
        <div className="col-auto">
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ minWidth: "250px" }}
          />
        </div>
        <div className="col-auto">
          <select
            className="form-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ minWidth: "140px" }}
          >
            <option value="All">All</option>
            {statusOptions.map((status) => (
              <option key={status.s_id} value={status.s_TranslatedText}>
                {status.s_TranslatedText}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="table-responsive" style={{ padding: "12px 16px" }}>
        <table className="table table-hover table-bordered table-striped align-middle">
          <thead className="table-dark text-center">
            <tr>
              <th>Full Name</th>
              <th>Username</th>
              <th>Role</th>
              <th>Email</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedData(getFilteredUsers()).map((u) => (
              <tr key={u.id}>
                <td className="fullname-cell" title={u.full_name}>
                  {u.full_name}
                </td>
                <td>{u.userid || "-"}</td>
                <td className="role-cell" title={renderEmpRoles(u.emp_roles)}>
                  <div className="role-text">{renderEmpRoles(u.emp_roles)}</div>
                </td>
                <td className="email-cell" title={u.email}>
                  <div className="email-text">{u.email}</div>
                </td>
                <td>
                  <span
                    className={`status-badge ${
                      u.status === 1 ? "active" : "inactive"
                    }`}
                    style={{
                      cursor: PERMISSIONS.users.edit
                        ? "pointer"
                        : "not-allowed",
                    }}
                    onClick={() => openStatusModal(u)}
                    title="Click to change status"
                  >
                    {u.status === 1 ? "Active" : "Inactive"}
                  </span>
                </td>
                <td className="actions-cell">
                  <div className="actions-wrapper">
                    {/* ✏️ Edit Button — requires Edit permission */}
                    <button
                      className="btn btn-warning btn-sm btn-action"
                      onClick={() => handleEdit(u.id)}
                      disabled={!PERMISSIONS.users.edit}
                    >
                      <FaEdit />
                    </button>

                    {/* 🗑️ Delete Button — requires Delete permission and cannot delete self */}
                    {PERMISSIONS.users.delete && (
                      <button
                        className="btn btn-danger btn-sm btn-action"
                        onClick={() => handleDelete(u.id)}
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          totalItems={getFilteredUsers().length}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>

      <Modal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        centered
        size="sm"
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Status</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Select
            value={newStatus}
            onChange={(e) => setNewStatus(Number(e.target.value))}
          >
            <option value={1}>Active</option>
            <option value={5}>Inactive</option>
          </Form.Select>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStatusModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveStatus}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

export default AdminUserPage;
